import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import LanguageFlags from "@/components/LanguageFlags";
import {
  ArrowLeft, Send, Shield, Award,
  Wrench, Flame, Cpu, Hammer, MoreHorizontal, UserSearch, Calendar, Star, Briefcase,
} from "lucide-react";
import CertifiedBadge from "@/components/CertifiedBadge";
import GoldBadge from "@/components/GoldBadge";
import CERT_TR from "@/i18n_cert";
import ImageUploadButton, { ImageThumbPreview } from "@/components/ImageUploadButton";

const TR = {
  sk: {
    back: "Späť",
    member_since: (y) => `Tigik member od ${y}`,
    years_exp: (n) => `${n}+ rokov praxe`,
    about: "O mne",
    specialization: "Špecializácia",
    dm_eyebrow: "Pošli súkromnú správu v Tigiku",
    dm_placeholder: (name) => `Napíš ${name}… Príklad: „Ahoj, môžeš mi poradiť s orbitálkou na DN80?"`,
    sending: "Odosielam…",
    send_btn: "Odoslať",
    msg_sent: "Správa odoslaná — pozri si svoju schránku v profile.",
    send_failed: "Nepodarilo sa poslať",
    load_failed: "Nepodarilo sa načítať profil",
    edit_own: "Toto je tvoj profil — uprav v nastaveniach",
    areas: { tig: "Ručný TIG", orbital: "Orbitálne", pipefitter: "Potrubár", prep: "Prípravár", recruiter: "Nábor & HR", other: "Iné" },
    levels: { beginner: "Začiatočník", experienced: "Skúsený", expert: "Expert" },
  },
  en: {
    back: "Back",
    member_since: (y) => `Tigik member since ${y}`,
    years_exp: (n) => `${n}+ years of experience`,
    about: "About",
    specialization: "Specialization",
    dm_eyebrow: "Send a private message on Tigik",
    dm_placeholder: (name) => `Write to ${name}… Example: "Hi, can you help me with an orbital DN80?"`,
    sending: "Sending…",
    send_btn: "Send",
    msg_sent: "Message sent — check your inbox in profile.",
    send_failed: "Failed to send",
    load_failed: "Could not load profile",
    edit_own: "This is your profile — edit in settings",
    areas: { tig: "Manual TIG", orbital: "Orbital", pipefitter: "Pipefitter", prep: "Preparator", recruiter: "Recruitment & HR", other: "Other" },
    levels: { beginner: "Beginner", experienced: "Experienced", expert: "Expert" },
  },
  cs: {
    back: "Zpět",
    member_since: (y) => `Tigik člen od ${y}`,
    years_exp: (n) => `${n}+ let praxe`,
    about: "O mně",
    specialization: "Specializace",
    dm_eyebrow: "Pošli soukromou zprávu v Tigiku",
    dm_placeholder: (name) => `Napiš ${name}… Příklad: „Ahoj, můžeš mi poradit s orbitálkou na DN80?"`,
    sending: "Odesílám…",
    send_btn: "Odeslat",
    msg_sent: "Zpráva odeslána — podívej se do schránky v profilu.",
    send_failed: "Nepodařilo se odeslat",
    load_failed: "Nepodařilo se načíst profil",
    edit_own: "Toto je tvůj profil — uprav v nastavení",
    areas: { tig: "Ruční TIG", orbital: "Orbitální", pipefitter: "Potrubář", prep: "Připravář", recruiter: "Nábor & HR", other: "Jiné" },
    levels: { beginner: "Začátečník", experienced: "Zkušený", expert: "Expert" },
  },
  de: {
    back: "Zurück",
    member_since: (y) => `Tigik-Mitglied seit ${y}`,
    years_exp: (n) => `${n}+ Jahre Erfahrung`,
    about: "Über mich",
    specialization: "Spezialisierung",
    dm_eyebrow: "Sende eine private Nachricht auf Tigik",
    dm_placeholder: (name) => `Schreib an ${name}… Beispiel: „Hi, kannst du mir bei Orbital DN80 helfen?"`,
    sending: "Senden…",
    send_btn: "Senden",
    msg_sent: "Nachricht gesendet — schau in dein Postfach im Profil.",
    send_failed: "Senden fehlgeschlagen",
    load_failed: "Profil konnte nicht geladen werden",
    edit_own: "Das ist dein Profil — bearbeite in Einstellungen",
    areas: { tig: "Manuelles WIG", orbital: "Orbital", pipefitter: "Rohrleger", prep: "Vorbereiter", recruiter: "Recruiting & HR", other: "Sonstiges" },
    levels: { beginner: "Anfänger", experienced: "Erfahren", expert: "Experte" },
  },
  pl: {
    back: "Wstecz",
    member_since: (y) => `Członek Tigik od ${y}`,
    years_exp: (n) => `${n}+ lat doświadczenia`,
    about: "O mnie",
    specialization: "Specjalizacja",
    dm_eyebrow: "Wyślij prywatną wiadomość na Tigiku",
    dm_placeholder: (name) => `Napisz do ${name}… Przykład: „Cześć, pomożesz mi z orbitalem DN80?"`,
    sending: "Wysyłam…",
    send_btn: "Wyślij",
    msg_sent: "Wiadomość wysłana — sprawdź skrzynkę w profilu.",
    send_failed: "Nie udało się wysłać",
    load_failed: "Nie udało się załadować profilu",
    edit_own: "To Twój profil — edytuj w ustawieniach",
    areas: { tig: "Ręczny TIG", orbital: "Orbitalny", pipefitter: "Rurociągowiec", prep: "Preparator", recruiter: "Rekrutacja & HR", other: "Inne" },
    levels: { beginner: "Początkujący", experienced: "Doświadczony", expert: "Ekspert" },
  },
  fr: {
    back: "Retour",
    member_since: (y) => `Membre Tigik depuis ${y}`,
    years_exp: (n) => `${n}+ ans d'expérience`,
    about: "À propos",
    specialization: "Spécialisation",
    dm_eyebrow: "Envoie un message privé dans Tigik",
    dm_placeholder: (name) => `Écris à ${name}… Exemple : « Salut, tu peux me conseiller sur l'orbital DN80 ? »`,
    sending: "Envoi…",
    send_btn: "Envoyer",
    msg_sent: "Message envoyé — consulte ta messagerie dans ton profil.",
    send_failed: "Échec de l'envoi",
    load_failed: "Échec du chargement du profil",
    edit_own: "C'est ton profil — modifie-le dans les paramètres",
    areas: { tig: "TIG manuel", orbital: "Orbital", pipefitter: "Tuyauteur", prep: "Préparateur", recruiter: "Recrutement & RH", other: "Autre" },
    levels: { beginner: "Débutant", experienced: "Expérimenté", expert: "Expert" },
  },
};

const AREA_ICONS = {
  tig: Flame, orbital: Cpu, pipefitter: Wrench, prep: Hammer, recruiter: UserSearch, other: MoreHorizontal,
};

const LEVEL_STYLE = {
  beginner:    { color: "text-zinc-300", border: "border-zinc-700" },
  experienced: { color: "text-blue-300", border: "border-blue-800" },
  expert:      { color: "text-amber-300", border: "border-amber-700" },
};

export default function PublicProfile() {
  const { userId } = useParams();
  const nav = useNavigate();
  const { user: me } = useAuth();
  const { lang } = useLang() || { lang: "sk" };
  const t = TR[lang] || TR.sk;
  const [profile, setProfile] = useState(null);
  const [err, setErr] = useState("");
  const [dmText, setDmText] = useState("");
  const [dmImage, setDmImage] = useState(null);
  const [dmBusy, setDmBusy] = useState(false);
  const [dmMsg, setDmMsg] = useState(null);

  useEffect(() => {
    setErr(""); setProfile(null);
    api.get(`/users/${userId}/public`)
      .then((r) => setProfile(r.data))
      .catch((e) => setErr(e.response?.data?.detail || t.load_failed));
  }, [userId, t.load_failed]);

  const sendDM = async (e) => {
    e.preventDefault();
    const text = dmText.trim();
    if (!text && !dmImage) return;
    setDmBusy(true); setDmMsg(null);
    try {
      await api.post("/dm/send", { to_user_id: userId, text, image_base64: dmImage });
      setDmText("");
      setDmImage(null);
      setDmMsg({ kind: "ok", text: t.msg_sent });
    } catch (e) {
      setDmMsg({ kind: "err", text: e.response?.data?.detail || t.send_failed });
    } finally { setDmBusy(false); }
  };

  if (err) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-16 text-center">
        <p className="text-red-400 mb-4">{err}</p>
        <button onClick={() => nav(-1)} className="wb-btn-ghost px-4 py-2 text-sm">{t.back}</button>
      </div>
    );
  }
  if (!profile) return null;

  const isSelf = me?.id === profile.id;
  const lvlStyle = profile.expertise_level ? LEVEL_STYLE[profile.expertise_level] : null;
  const lvlLabel = profile.expertise_level ? t.levels[profile.expertise_level] : null;
  const joinedYear = profile.joined_at ? new Date(profile.joined_at).getFullYear() : null;

  return (
    <div data-testid="public-profile" className="max-w-3xl mx-auto px-6 py-10">
      <button
        onClick={() => nav(-1)}
        data-testid="back-btn"
        className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-zinc-500 hover:text-gold-400 mb-6"
      >
        <ArrowLeft className="w-3 h-3" /> {t.back}
      </button>

      {/* Hero card */}
      <div className="border border-zinc-800 bg-[var(--wb-surface)] p-6 md:p-8 mb-6">
        <div className="flex items-start gap-5 flex-wrap">
          {profile.picture ? (
            <img
              src={profile.picture}
              alt={profile.name}
              className="w-24 h-24 md:w-28 md:h-28 object-cover border border-gold-700"
            />
          ) : (
            <div className="w-24 h-24 md:w-28 md:h-28 bg-zinc-900 border border-zinc-700 flex items-center justify-center text-3xl font-display font-black text-zinc-500">
              {(profile.name || "?").slice(0, 1).toUpperCase()}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h1 className="font-display font-black text-3xl md:text-4xl flex items-center gap-3 flex-wrap">
              {profile.cert_holder_name || profile.name}
              {profile.preferred_languages?.length > 0 && (
                <LanguageFlags languages={profile.preferred_languages} size="md" />
              )}
              {profile.role === "admin" && (
                <span className="wb-tag !text-gold-300 !border-gold-500 inline-flex items-center gap-1">
                  <Shield className="w-3 h-3" /> ADMIN
                </span>
              )}
              {profile.is_subscribed && profile.role !== "admin" && (
                <span className="wb-tag !text-gold-400 !border-gold-600">
                  {(profile.plan_tier || "PRO").toUpperCase()}
                </span>
              )}
              {profile.plan_tier === "gold" && (
                <GoldBadge size="md" />
              )}
              {profile.certified && (
                <CertifiedBadge size="sm" showLabel certificateNo={profile.certificate_no} />
              )}
            </h1>
            {profile.custom_title && (
              <div className={`mt-2 text-sm font-mono uppercase tracking-wider ${profile.custom_title_color === "purple" ? "text-purple-300" : "text-gold-400"}`}>
                {profile.custom_title}
              </div>
            )}
            <div className="flex flex-wrap gap-3 mt-4 text-xs text-zinc-400">
              {joinedYear && (
                <span className="inline-flex items-center gap-1.5">
                  <Calendar className="w-3 h-3 text-gold-500" /> {t.member_since(joinedYear)}
                </span>
              )}
              {profile.years_experience != null && (
                <span className="inline-flex items-center gap-1.5">
                  <Briefcase className="w-3 h-3 text-gold-500" /> {t.years_exp(profile.years_experience)}
                </span>
              )}
              {lvlStyle && lvlLabel && (
                <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 border ${lvlStyle.border} ${lvlStyle.color}`}>
                  <Star className="w-3 h-3" /> {lvlLabel}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Full certificate card — shown when user is certified */}
        {profile.certified && (
          <div className="mt-6 pt-5 border-t border-zinc-800" data-testid="certificate-card">
            <CertifiedBadge size="lg" certificateNo={profile.certificate_no} />
            {profile.certified_since && (() => {
              const ct = CERT_TR[lang] || CERT_TR.sk;
              const dateLocaleMap = { sk: "sk-SK", en: "en-GB", cs: "cs-CZ", de: "de-DE", pl: "pl-PL" };
              const since = new Date(profile.certified_since).toLocaleDateString(dateLocaleMap[lang] || "sk-SK", {
                day: "2-digit", month: "long", year: "numeric",
              });
              return (
                <div
                  data-testid="lifetime-cert-line"
                  className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 border border-amber-600/40 bg-gradient-to-r from-amber-500/10 via-amber-400/5 to-amber-500/10 text-[11px] md:text-xs"
                  style={{ boxShadow: "0 0 18px -8px rgba(251,191,36,0.5)" }}
                >
                  <Star className="w-3.5 h-3.5 text-amber-300" strokeWidth={2.5} />
                  <span className="font-display font-black uppercase tracking-widest text-amber-300">{ct.lifetime_label}</span>
                  <span className="text-amber-100/80">{ct.lifetime_brand}</span>
                  <span className="text-zinc-500">·</span>
                  <span className="font-mono text-amber-200">{ct.lifetime_since} {since}</span>
                </div>
              );
            })()}
          </div>
        )}

        {/* Bio */}
        {profile.bio && (
          <div className="mt-6 pt-5 border-t border-zinc-800">
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">{t.about}</div>
            <p className="text-sm text-zinc-300 whitespace-pre-wrap leading-relaxed">{profile.bio}</p>
          </div>
        )}

        {/* Expertise areas */}
        {profile.expertise_areas?.length > 0 && (
          <div className="mt-6 pt-5 border-t border-zinc-800">
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-3">{t.specialization}</div>
            <div className="flex flex-wrap gap-2">
              {profile.expertise_areas.map((a) => {
                const Icon = AREA_ICONS[a] || AREA_ICONS.other;
                const areaLabel = t.areas[a] || t.areas.other;
                return (
                  <span
                    key={a}
                    data-testid={`expertise-${a}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-gold-700/40 bg-gold-950/20 text-xs text-gold-300"
                  >
                    <Icon className="w-3 h-3" /> {areaLabel}
                  </span>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Actions: DM only */}
      {!isSelf && (
        <>
          {/* DM form */}
          <form
            onSubmit={sendDM}
            data-testid="dm-form"
            className="border border-zinc-800 bg-[var(--wb-surface)] p-5"
          >
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">
              {t.dm_eyebrow}
            </div>
            <textarea
              value={dmText}
              onChange={(e) => setDmText(e.target.value)}
              data-testid="dm-textarea"
              placeholder={t.dm_placeholder(profile.name)}
              maxLength={2000}
              rows={3}
              className="w-full bg-black border border-zinc-700 px-3 py-2 text-sm text-white placeholder-zinc-600 focus:border-gold-600 outline-none resize-none"
            />
            {dmImage && (
              <div className="mt-2">
                <ImageThumbPreview src={dmImage} onClear={() => setDmImage(null)} />
              </div>
            )}
            <div className="flex items-center justify-between mt-3 flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <ImageUploadButton onPicked={setDmImage} testid="dm-image-btn" />
                <span className="text-[10px] font-mono text-zinc-600">
                  {dmText.length}/2000
                </span>
              </div>
              <button
                type="submit"
                disabled={dmBusy || (!dmText.trim() && !dmImage)}
                data-testid="dm-send-btn"
                className="wb-btn-primary px-5 py-2 text-xs inline-flex items-center gap-2 disabled:opacity-50"
              >
                <Send className="w-3 h-3" /> {dmBusy ? t.sending : t.send_btn}
              </button>
            </div>
            {dmMsg && (
              <div className={`mt-3 text-xs ${dmMsg.kind === "ok" ? "text-green-400" : "text-red-400"}`}>
                {dmMsg.text}
              </div>
            )}
          </form>
        </>
      )}

      {isSelf && (
        <Link
          to="/profile"
          data-testid="edit-own-profile"
          className="wb-btn-ghost px-5 py-3 text-sm inline-flex items-center justify-center gap-2 w-full sm:w-auto"
        >
          {t.edit_own}
        </Link>
      )}
    </div>
  );
}
